import { AchievementsManager } from "./AchievementsManager";
import { AudioManager } from "./AudioManager";
import { GameConstants } from "./GameConstants";
import { GameVars } from "./GameVars";
import { Day, Language } from "./Types";
import { UnlockedLevelContainer } from "./scenes/board-scene/UnlockedLevelContainer";
import { SplashScene } from "./scenes/splash-scene/SplashScene";
import { CategoriesContainer } from "./scenes/splash-scene/categories/CategoriesContainer";

export class GameManager {

    public static init(): void {

        GameVars.secretMode = false;
        GameVars.justUnlockedBoardsData = [];   

        if (!GameVars.isMobile) {

            GameVars.scaleX = 1;

        } else {

            GameVars.currentScene.game.scale.displaySize = GameVars.currentScene.game.scale.parentSize;
            GameVars.currentScene.game.scale.refresh();

            const aspectRatio = window.innerWidth / window.innerHeight;
            GameVars.scaleX = (GameConstants.GAME_WIDTH / GameConstants.GAME_HEIGHT) / aspectRatio;
        }

        const date = new Date();

        GameVars.today = { day: date.getDate(), month: date.getMonth() + 1, year: date.getFullYear() };

        GameManager.readGameData();
    }

    public static readGameData(): void {

        GameManager.getGameStorageData(
            GameConstants.SAVED_GAME_DATA_KEY,
            function (gameData: string): void {

                if (gameData) {

                    GameVars.gameData = GameConstants.TESTING_GAME_OVER ? GameConstants.TESTING_STORED_DATA : JSON.parse(gameData);
                    GameVars.gameData.firstSession = false;

                    // POR COMPATIBILIDAD CON VERSIONES ANTERIORES QUE NO TENIAN MULTIDIOMA
                    if (!GameVars.gameData.data.language) {
                        GameVars.gameData.data.language = GameManager.getBrowserLanguage();
                        GameManager.writeGameData();
                    }

                    // SI LA PRIMERA ACCION NO ES INIT debido a un bug anterior
                    if (GameVars.gameData.data.savedActions && GameVars.gameData.data.savedActions.length > 0 && GameVars.gameData.data.savedActions[0].type !== "init") {
                        GameVars.gameData.data.savedActions = null;
                        GameManager.writeGameData();
                    }

                } else {
                    GameVars.gameData = {
                        muted: false,
                        firstSession: true,
                        categoriesData: null,
                        savedState: null,
                        data: {
                            fxMuted: false,
                            language: GameManager.getBrowserLanguage(),
                            savedActions: []
                        }
                    };
                }

                GameManager.startGame();
            }
        );
    }

    public static setCurrentScene(scene: Phaser.Scene): void {

        GameVars.currentScene = scene;
    }

    public static onGameAssetsLoaded(): void {

        AudioManager.init();

        // HAY 662 NIVELES
        GameVars.boardsData = GameVars.currentScene.cache.json.get("boards");

        if (!GameVars.gameData.categoriesData) {

            GameVars.gameData.categoriesData = {};
        
            GameVars.boardsData.forEach(function (boardData: any): void {
                if (GameVars.gameData.categoriesData[boardData.cat]) {
                    GameVars.gameData.categoriesData[boardData.cat].n += 1;
                } else {
                    GameVars.gameData.categoriesData[boardData.cat] = {n: 1, levelsData: []};
                }
            });

            GameManager.writeGameData();
        }

        if (GameVars.gameData.data.savedActions && GameVars.gameData.data.savedActions.length > 0) {
            GameVars.restoredSession = true;
            SplashScene.instantiateBrandingLayer = false;
            GameManager.enterBoardScene(null, false);
        } else {
            GameVars.restoredSession = false;
            GameManager.enterSplashScene();
        }  

        (window as any).CrazyGames.SDK.game.sdkGameLoadingStop();
    }

    public static activateSecretMode(): void {  

        GameVars.secretMode = true;
    }

    public static enterSplashScene(): void {

        GameVars.gameData.data.savedActions = [];
        GameManager.writeGameData();

        // TODO: CALCULAR LOS NIVELS DESBLOQUEADOS
        const categories = [...CategoriesContainer.CATEGORIES_DATA];
        GameVars.unlockedBoards = 0;

        categories.forEach((category) => {
    
            for (let i = 0; i < GameVars.gameData.categoriesData[category.name].levelsData.length; i++) {
    
                if (GameVars.gameData.categoriesData[category.name].levelsData[i].time > 0) {
                    GameVars.unlockedBoards ++;
                }
            }
        });

        GameVars.currentScene.scene.start("SplashScene");
    }

    public static goBack(): void {
            
        GameVars.gameData.firstSession = false;
       
        GameManager.enterSplashScene();
    }

    public static resetCalendar(): void {

        GameVars.boardsData.forEach(function (boardData: any): void {
            GameVars.gameData.categoriesData[boardData.cat].levelsData = [];
        });

        GameManager.writeGameData();

        GameManager.enterSplashScene();
    }

    public static onDaySelected(currentDay: Day): void {

        GameVars.levelIndex = GameManager.getLevelIndex(currentDay);

        GameVars.levelReset = false;

        GameVars.currentDay = currentDay;

        GameManager.enterBoardScene(null, true);  
    }

    public static nextLevel(): void {

        GameVars.levelReset = false;

        if (!GameVars.currentDay || (GameVars.currentDay.day === GameVars.today.day && GameVars.currentDay.month === GameVars.today.month && GameVars.currentDay.year === GameVars.today.year)) {
           
            GameManager.enterSplashScene();

        } else {

            // SUMARLE 1 DIA A LA FECHA ACTUAL
            GameVars.currentDay = GameManager.getNextDay(GameVars.currentDay);

            GameVars.levelIndex = GameManager.getLevelIndex(GameVars.currentDay);

            const cat = GameVars.boardsData[GameVars.levelIndex].cat;

            const levelsData = GameVars.gameData.categoriesData[cat].levelsData;

            let insertLevelData = true;

            for (let i = 0; i < levelsData.length; i++) {
                if (levelsData[i].id === GameVars.boardsData[GameVars.levelIndex].id) {
                    insertLevelData = false;
                    break;
                }
            }

            if (insertLevelData) {
                levelsData.push({id: GameVars.boardsData[GameVars.levelIndex].id, highScore: 0, time: 0});
                GameManager.writeGameData();
            }
            
            GameManager.enterBoardScene(null, false);
        }

        (window as any).CrazyGames.SDK.game.gameplayStop();
    }

    public static getLevelIndex(d: Day): number {

        const { day, month, year } = d;

        const dateObject = new Date(year, month - 1, day); // Restamos 1 al mes porque en JavaScript los meses van de 0 a 11.

        // Calcula el día del año empezando por 0 (0-365)
        const startOfYear = new Date(year, 0, 1);
        const millisecondsPerDay = 24 * 60 * 60 * 1000;

        // UN NUMERO DEL 0 AL 365
        return Math.floor((dateObject.getTime() - startOfYear.getTime()) / millisecondsPerDay);
    }

    public static getNextDay(date: Day): Day {

        const { day, month, year } = date;
        
        // Obtiene el último día del mes actual
        const lastDayOfMonth = new Date(year, month - 1, 0).getDate();
        
        // Comprueba si es el último día del mes
        if (day < lastDayOfMonth) {
            // Si no es el último día, simplemente incrementa el día en 1
            return { day: day + 1, month, year };
        } else {
            // Si es el último día, verifica si es el último día del año
            if (month < 12) {
                // Si no es el último mes del año, avanza al primer día del siguiente mes
                return { day: 1, month: month + 1, year };
            } else {
                // Si es el último mes del año, avanza al primer día del próximo año
                return { day: 1, month: 1, year: year + 1 };
            }
        }
    }

    public static enterBoardScene(id?: string, fromSelectionLevelScene?: boolean): void {

        if (id) {

            GameVars.levelIndex = GameVars.boardsData.findIndex(function (boardData: any): boolean {
                return boardData.id === id;
            });

            GameVars.currentDay = null;
        }

        if (fromSelectionLevelScene) {
            GameVars.justUnlockedBoardsData = [];
        }

        GameVars.currentScene.scene.start("BoardScene");
    }

    public static resetGame(): void {

        GameManager.enterBoardScene();
    }

    public static writeGameData(): void {

        GameManager.setGameStorageData(
            GameConstants.SAVED_GAME_DATA_KEY,
            GameVars.gameData,
            function (): void {
                //
            }
        );
    }

    public static unlockRandomBoard(): void {

        let unlockedBoardIndex = 0;

        let cat = GameVars.boardsData[unlockedBoardIndex].cat;

        let levelsData = GameVars.gameData.categoriesData[cat].levelsData;

        let alreadyUnlocked = false;

        for (let i = 0; i < levelsData.length; i++) {
            if (levelsData[i].id === GameVars.boardsData[unlockedBoardIndex].id) {
                alreadyUnlocked = true;
                break;
            }
        }

        while (alreadyUnlocked && unlockedBoardIndex < GameVars.boardsData.length - 1) {

            unlockedBoardIndex ++;

            cat = GameVars.boardsData[unlockedBoardIndex].cat;

            levelsData = GameVars.gameData.categoriesData[cat].levelsData;

            alreadyUnlocked = false;

            for (let i = 0; i < levelsData.length; i++) {
                if (levelsData[i].id === GameVars.boardsData[unlockedBoardIndex].id) {
                    alreadyUnlocked = true;
                    break;
                }
            }
        }

        if (unlockedBoardIndex === GameVars.boardsData.length - 1) {

            GameVars.unlockedBoardIndex = null;

        } else {

            GameVars.unlockedBoardIndex = unlockedBoardIndex;

            const cat = GameVars.boardsData[GameVars.unlockedBoardIndex].cat;

            const levelsData = GameVars.gameData.categoriesData[cat].levelsData;

            levelsData.push({id: GameVars.boardsData[GameVars.unlockedBoardIndex].id, highScore: 0, time: 0});
            GameManager.writeGameData(); 

            GameVars.justUnlockedBoardsData.push(cat);
        }

        UnlockedLevelContainer.currentInstance.onRewardedAdSuccess();
    }

    public static setLanguage(lang: Language): void {

        GameVars.gameData.data.language = lang;
        GameManager.writeGameData();

        GameManager.enterSplashScene();
    }

    private static startGame(): void {

        AchievementsManager.init();

        GameVars.currentScene.scene.start("PreloadScene");

        (window as any).CrazyGames.SDK.game.sdkGameLoadingStart();
    }

    private static getBrowserLanguage(): string {

        const lang = navigator.language.slice(0, 2) || navigator['userLanguage'];

        switch (lang) {

            case Language.EN:
            case Language.ES:
            case Language.CA:
            case Language.DE:
            case Language.FR:
            case Language.IT:
            case Language.PT:
            case Language.NL:
            case Language.RU:
            case Language.JA:
                return lang;
            default:
                return Language.EN; // Retorna 'en' para cualquier otra variante del inglés o idiomas no listados
        }
    }
    
    private static getGameStorageData(key: string, successCb: Function): void {

        const gameDataStr = localStorage.getItem(key);
        successCb(gameDataStr);
    }

    private static setGameStorageData(key: string, value: any, successCb: Function): void {

        localStorage.setItem(key, JSON.stringify(value));
        successCb();
    }
}
